import { Injectable, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { defaultSelectOption, SelectExtendedOption as CoreSelectExtendedOption, SelectOption as CoreSelectOption } from '@core';
import { incidentColumnOptions, IncidentModel, IncidentSeverity, IncidentTabStatusFilter, NormalizedIncident, normalizeIncidents } from '@models';
import { AvsDateRangeIds } from '@ui';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * TODO: remove these interfaces all together with the service
 * These interfaces are created to extend the SelectOption and SelectExtendedOption
 * with id property without affecting other modules that use these types.
 * Some of the dependent modules require ids to be selected instead of labels.
 */
interface SelectOption extends CoreSelectOption {
  id?: string;
}

interface SelectExtendedOption extends CoreSelectExtendedOption {
  id?: string;
}

/**
 * TODO: refactor the service to use ngrx store or ngrx-signal store
 * @deprecated
 */
@Injectable({
  providedIn: 'root',
})
export class IncidentsFilterService {
  public filtersApplied = signal(false);
  initialResolvedDateStart: Date | null = null;
  initialResolvedDateEnd: Date | null = null;
  displayedColumns: string[] = incidentColumnOptions.map(option => option.value);
  incidentsData: IncidentModel[] = [];
  resolvedIncidentsData: IncidentModel[] = [];
  isBuildingTable = false;
  isFloorTable = false;
  isRoomTable = false;
  isReportMode = false;
  incidentTabStatusFilterEnum = IncidentTabStatusFilter;
  normalizedIncidents: NormalizedIncident[] = [];
  criticalIncidentsAmount = 0;
  majorIncidentsAmount = 0;
  minorIncidentsAmount = 0;
  columnSelect = new FormControl();
  search = '';
  deviceTypeOptions: SelectExtendedOption[] = [];
  assignedUserValue: string[] = [];
  severityOptions: SelectExtendedOption[] = [];
  buildingOptions: SelectOption[] = [];
  floorOptions: SelectOption[] = [];
  roomOptions: SelectOption[] = [];
  // resolved incidents filters
  resolvedDateRangeId = AvsDateRangeIds.TODAY;
  resolvedDateRange = new FormGroup({
    end: new FormControl<Date | null>(null),
    start: new FormControl<Date | null>(null),
  });
  lastRequestedResolvedStart = '';
  lastRequestedResolvedEnd = '';
  resolvedIncidentsDeviceTypeOptions: SelectExtendedOption[] = [];
  resolvedIncidentsBuildingOptions: SelectOption[] = [];
  resolvedIncidentsFloorOptions: SelectOption[] = [];
  resolvedIncidentsRoomOptions: SelectOption[] = [];
  resolvedFiltersInitialized = false;
  columnOptions = incidentColumnOptions;
  resolvedIncidentsLoading = false;

  resolvedDates$ = new BehaviorSubject<{ endDateTime: string; startDateTime: string; }>({
    endDateTime: '',
    startDateTime: '',
  });

  filtersChanged$ = new Subject<void>();

  get selectedBuilding() {
    return this._selectedBuilding;
  }

  get selectedBuildingId() {
    const selectedBuildingOption = this.buildingOptions.find(
      ({ title }) => title === this.selectedBuilding,
    );

    return selectedBuildingOption?.id;
  }

  get incidentTabStatusFilter() {
    return this._incidentTabStatusFilter;
  }

  get selectedFloor() {
    return this._selectedFloor;
  }

  get selectedFloorId() {
    const selectedFloorOption = this.floorOptions.find(
      ({ value }) => value === this.selectedFloor,
    );

    return selectedFloorOption?.id;
  }

  get selectedRoom() {
    return this._selectedRoom;
  }

  get selectedRoomId() {
    const selectedRoomOption = this.roomOptions.find(
      ({ value }) => value === this.selectedRoom,
    );

    return selectedRoomOption?.id;
  }

  get resolvedIncidentsSelectedFloor() {
    return this._resolvedIncidentsSelectedFloor;
  }

  get resolvedIncidentsSelectedRoom() {
    return this._resolvedIncidentsSelectedRoom;
  }


  get resolvedIncidentsSelectedBuilding() {
    return this._resolvedIncidentsSelectedBuilding;
  }

  get deviceTypeValue() {
    return this.deviceTypeOptions.filter(({ checked }) => checked).map(({ value }) => value);
  }

  get deviceTypeIds() {
    return this.deviceTypeOptions.filter(({ checked }) => checked).map(({ id }) => id!);
  }

  get severityFilter() {
    return this.severityOptions.filter(({ checked }) => checked).map(({ value }) => value);
  }


  get selectedFilters(): boolean {
    return Boolean(
      (this.isBuildingTable ? false : this.selectedBuilding) ||
      (this.isFloorTable ? false : this.selectedFloor) ||
      (this.isRoomTable ? false : this.selectedRoom) ||
      this.severityFilter?.length ||
      this.assignedUserValue?.length ||
      this.deviceTypeValue?.length,
    );
  }

  get resolvedIncidentsDeviceTypeValue() {
    return this.resolvedIncidentsDeviceTypeOptions.filter(({ checked }) => checked).map(({ value }) => value);
  }

  get resolvedIncidentsSelectedFilters(): boolean {
    return Boolean(
      (this.isBuildingTable ? false : this.resolvedIncidentsSelectedBuilding) ||
      (this.isFloorTable ? false : this.resolvedIncidentsSelectedFloor) ||
      (this.isRoomTable ? false : this.resolvedIncidentsSelectedRoom) ||
      this.resolvedIncidentsDeviceTypeValue?.length,
    );
  }

  set incidentTabStatusFilter(value: IncidentTabStatusFilter) {
    if (this._incidentTabStatusFilter === value) {
      return;
    }
    this._incidentTabStatusFilter = value;
    this.search = '';
    this.setColumnsValues();

    if (value === IncidentTabStatusFilter.HISTORY) {
      this.resolvedFiltersInitialized = true;
      this.refreshResolvedIncidentsTable();
    }

    if (value === IncidentTabStatusFilter.OPEN) {
      this.resolvedFiltersInitialized = false;
      this.refreshOpenIncidentsTable();
    }
  }


  set selectedBuilding(building: string) {
    if (this._selectedBuilding === building) {
      return;
    }

    this._selectedBuilding = building;
    this.selectedFloor = '';
    this.selectedRoom = '';
    this.floorOptions = this.generateFloorOptions(this.normalizedIncidents, this.selectedBuilding);
    this.collectOpenIncidentsFilterData();
    this.filterOpenIncidentsTable();

    this.filtersChanged$.next();
  }


  set selectedFloor(floor: string) {
    if (this._selectedFloor === floor) {
      return;
    }

    this._selectedFloor = floor;
    this.selectedRoom = '';
    this.roomOptions = this.generateRoomOptions(this.normalizedIncidents, this.selectedFloor);
    this.collectOpenIncidentsFilterData();
    this.filterOpenIncidentsTable();

    this.filtersChanged$.next();
  }


  set selectedRoom(room: string) {
    if (this._selectedRoom === room) {
      return;
    }

    this._selectedRoom = room;
    this.collectOpenIncidentsFilterData();
    this.filterOpenIncidentsTable();

    this.filtersChanged$.next();
  }


  set resolvedIncidentsSelectedBuilding(building: string) {
    if (this._resolvedIncidentsSelectedBuilding === building) {
      return;
    }

    this._resolvedIncidentsSelectedBuilding = building;
    this.resolvedIncidentsSelectedFloor = '';
    this.resolvedIncidentsSelectedRoom = '';
    this.resolvedIncidentsFloorOptions = this.generateFloorOptions(
      this.normalizedIncidents,
      this.resolvedIncidentsSelectedBuilding,
    );
    this.collectResolvedIncidentsFilterData();
    this.filterResolvedIncidentsTable();
  }


  set resolvedIncidentsSelectedFloor(floor: string) {
    if (this._resolvedIncidentsSelectedFloor === floor) {
      return;
    }

    this._resolvedIncidentsSelectedFloor = floor;
    this.resolvedIncidentsSelectedRoom = '';
    this.resolvedIncidentsRoomOptions = this.generateRoomOptions(
      this.normalizedIncidents,
      this.resolvedIncidentsSelectedFloor,
    );
    this.collectResolvedIncidentsFilterData();
    this.filterResolvedIncidentsTable();
  }


  set resolvedIncidentsSelectedRoom(room: string) {
    if (this._resolvedIncidentsSelectedRoom === room) {
      return;
    }

    this._resolvedIncidentsSelectedRoom = room;
    this.collectResolvedIncidentsFilterData();
    this.filterResolvedIncidentsTable();
  }


  set severityFilter(filter: string[]) {
    this.severityOptions = this.severityOptions.map(option => {
      option.checked = filter.includes(option.value);

      return option;
    });
    this.filterOpenIncidentsTable();
  }


  set deviceTypeValue(filter: string[]) {
    this.deviceTypeOptions = this.deviceTypeOptions.map(option => {
      option.checked = filter.includes(option.value);

      return option;
    });
    this.filterOpenIncidentsTable();
  }


  set resolvedIncidentsDeviceTypeValue(filter: string[]) {
    this.resolvedIncidentsDeviceTypeOptions = this.resolvedIncidentsDeviceTypeOptions.map(option => {
      option.checked = filter.includes(option.value);

      return option;
    });
    this.filterResolvedIncidentsTable();
  }


  private _filteredIncidents = new BehaviorSubject<NormalizedIncident[]>([]);
  private _resolvedIncidentsSelectedRoom = '';
  private _selectedBuilding = '';
  private _incidentTabStatusFilter: IncidentTabStatusFilter = IncidentTabStatusFilter.OPEN;
  private _selectedFloor = '';
  private _resolvedIncidentsSelectedFloor = '';
  private _resolvedIncidentsSelectedBuilding = '';
  private _selectedRoom = '';
  filteredIncidents$: Observable<NormalizedIncident[]> = this._filteredIncidents.asObservable();


  initFilterTableType({
    isRoomTable,
    isFloorTable,
    isBuildingTable,
    displayedColumns,
  }: {
    displayedColumns: string[];
    isBuildingTable?: boolean;
    isFloorTable?: boolean;
    isRoomTable?: boolean;
  }) {
    this.displayedColumns = displayedColumns;

    if (isRoomTable) {
      this.isRoomTable = true;
      this.isFloorTable = true;
      this.isBuildingTable = true;
    }

    if (isFloorTable) {
      this.isFloorTable = true;
      this.isBuildingTable = true;
    }

    if (isBuildingTable) {
      this.isBuildingTable = true;
    }

    if (this.isBuildingTable) {
      this.columnOptions = this.columnOptions.filter(column => column.value !== 'building');
      this.displayedColumns = this.displayedColumns.filter(column => column !== 'building');
    }

    if (this.isFloorTable) {
      this.columnOptions = this.columnOptions.filter(column => column.value !== 'floor');
      this.displayedColumns = this.displayedColumns.filter(column => column !== 'floor');
    }

    if (this.isRoomTable) {
      this.columnOptions = this.columnOptions.filter(column => column.value !== 'room');
      this.displayedColumns = this.displayedColumns.filter(column => column !== 'room');
    }

    const initialColumns = this.displayedColumns.filter(column => column !== 'index' && column !== 'ticketNumber');

    this.columnSelect.setValue(initialColumns);
    this.setColumnsValues();
    this.refreshOpenIncidentsTable();
  }


  initOpenIncidents(openIncidents: IncidentModel[]) {
    this.incidentsData = openIncidents;
    this.refreshIncidentCounter();
    this.refreshOpenIncidentsTable();
  }

  initResolvedIncidents(resolvedIncidents: IncidentModel[]) {
    this.resolvedIncidentsData = resolvedIncidents;
    this.refreshResolvedIncidentsTable();
  }

  refreshOpenIncidentsTable() {
    if (this.incidentTabStatusFilter === IncidentTabStatusFilter.OPEN) {
      this.refreshIncidentCounter();
      this.collectOpenIncidentsFilterData();
      this.setColumnsValues();
      this.filterOpenIncidentsTable();
    }
  }

  refreshIncidentCounter() {
    this.normalizedIncidents = normalizeIncidents(this.incidentsData);
    this.criticalIncidentsAmount = this.incidentsData.filter(
      incident => incident.severity === IncidentSeverity.CRITICAL,
    ).length;
    this.majorIncidentsAmount = this.incidentsData.filter(
      incident => incident.severity === IncidentSeverity.MAJOR,
    ).length;
    this.minorIncidentsAmount = this.incidentsData.filter(
      incident => incident.severity === IncidentSeverity.MINOR,
    ).length;
  }

  refreshResolvedIncidentsTable() {
    if (this.incidentTabStatusFilter === IncidentTabStatusFilter.HISTORY) {
      this.normalizedIncidents = normalizeIncidents(this.resolvedIncidentsData);
      this.collectResolvedIncidentsFilterData();
      this.setColumnsValues();
      this.filterResolvedIncidentsTable();
    }
  }

  setColumnsValues() {
    const selectedColumns = this.columnSelect.value;
    const currentTab = this.incidentTabStatusFilter;

    if (currentTab === IncidentTabStatusFilter.OPEN) {
      this.columnOptions = this.columnOptions.filter(({ value }) => value !== 'resolved');
      if (this.displayedColumns.includes('resolved')) {
        this.displayedColumns = this.displayedColumns.filter(column => column !== 'resolved');
      }
    } else {
      if (!this.columnOptions.find(({ value }) => value === 'resolved')) {
        const resolvedOption = incidentColumnOptions.find(({ value }) => value === 'resolved');

        if (resolvedOption) {
          const reportedIdx = this.columnOptions.findIndex(({ value }) => value === 'reported');

          this.columnOptions.splice(reportedIdx + 1, 0, resolvedOption);
        }

        const reportedColumnIdx = this.displayedColumns.indexOf('reported');

        if (!this.displayedColumns.includes('resolved')) {
          if (reportedColumnIdx !== -1) {
            this.displayedColumns.splice(reportedColumnIdx + 1, 0, 'resolved');
          } else {
            this.displayedColumns.push('resolved');
          }
        }
      }
    }

    if (selectedColumns?.length) {
      if (currentTab === IncidentTabStatusFilter.OPEN) {
        const filteredSelected = selectedColumns.filter((column: string) => column !== 'resolved');

        if (JSON.stringify(this.columnSelect.value) !== JSON.stringify(filteredSelected)) {
          this.columnSelect.setValue(filteredSelected);
        }
        const expectedDisplayed = this.generateTableColumns(this.columnSelect.value);

        if (JSON.stringify(this.displayedColumns) !== JSON.stringify(expectedDisplayed)) {
          this.displayedColumns = expectedDisplayed;
        }
      } else {
        const selected = [...selectedColumns];

        if (!selected.includes('resolved')) {
          const reportedIdx = selected.indexOf('reported');

          if (reportedIdx !== -1) {
            selected.splice(reportedIdx + 1, 0, 'resolved');
          } else {
            selected.push('resolved');
          }
        }

        if (JSON.stringify(this.columnSelect.value) !== JSON.stringify(selected)) {
          this.columnSelect.setValue(selected);
        }
        const expectedDisplayed = this.generateTableColumns(this.columnSelect.value);

        if (JSON.stringify(this.displayedColumns) !== JSON.stringify(expectedDisplayed)) {
          this.displayedColumns = expectedDisplayed;
        }
      }
    } else if (!this.displayedColumns.includes('index') || !this.displayedColumns.includes('ticketNumber')) {
      const defaultSelected = this.displayedColumns.filter(column => column !== 'index' && column !== 'ticketNumber');

      this.columnSelect.setValue(defaultSelected);
      this.displayedColumns = this.generateTableColumns(defaultSelected);
    }
  }

  ticketNumberSearch($event: string) {
    this.search = $event;
    if (this.incidentTabStatusFilter === IncidentTabStatusFilter.OPEN) {
      this.filterOpenIncidentsTable();
    } else {
      this.filterResolvedIncidentsTable();
    }
  }

  columnsChange(event: MatSelectChange) {
    this.columnSelect.setValue(event.value);
    this.displayedColumns = this.generateTableColumns(event.value);
  }

  // resolved incidents methods

  generateTableColumns(values: string[] = []) {
    return ['index', 'ticketNumber', ...values];
  }

  clearOpenIncidentsFilters() {
    if (!this.isRoomTable) {
      this.selectedRoom = '';
    }
    if (!this.isFloorTable) {
      this.selectedFloor = '';
    }
    if (!this.isBuildingTable) {
      this.selectedBuilding = '';
    }
    this.deviceTypeValue = [];
    this.assignedUserValue = [];
    this.severityFilter = [];
    this.filterOpenIncidentsTable();
  }

  updateDeviceTypeValue(filter: { checked: boolean; value: string; }) {
    this.deviceTypeOptions = this.deviceTypeOptions.map(option => {
      if (option.value === filter.value) {
        option.checked = filter.checked;
      }

      return option;
    });
    this.filterOpenIncidentsTable();
  }

  multiSelectChange(
    name:
      | 'deviceTypeValue'
      | 'assignedUserValue'
      | 'severityFilter'
      | 'resolvedIncidentsDeviceTypeValue',
    value: string[],
  ) {
    this[name] = value;
    if (this.incidentTabStatusFilter === IncidentTabStatusFilter.OPEN) {
      this.filterOpenIncidentsTable();
    } else {
      this.filterResolvedIncidentsTable();
    }

    this.filtersChanged$.next();
  }

  filterOpenIncidentsTable() {
    const filteredOpenIncidents = this.normalizedIncidents.filter(incident => {

      const severityMatch = !this.severityFilter.length || this.severityFilter.includes(incident.severity);

      const incidentNumberMatch =
        !this.search || incident.ticketNumber.toLowerCase().includes(this.search.toLowerCase());
      const deviceTypeMatch = !this.deviceTypeValue.length || this.deviceTypeValue.includes(incident.deviceType);
      const buildingMatch =
        !this.selectedBuilding || incident.building.toLowerCase().includes(this.selectedBuilding.toLowerCase());
      const floorMatch = !this.selectedFloor || incident.floor.toLowerCase().includes(this.selectedFloor.toLowerCase());
      const roomMatch = !this.selectedRoom || incident.room.toLowerCase().includes(this.selectedRoom.toLowerCase());

      return (
        severityMatch &&
        incidentNumberMatch &&
        deviceTypeMatch &&
        buildingMatch &&
        floorMatch &&
        roomMatch
      );
    });

    this.applyFiltersStatus();
    this._filteredIncidents.next(filteredOpenIncidents);
  }

  collectOpenIncidentsFilterData() {
    const deviceTypeOptions = new Map<string, SelectExtendedOption>();
    const buildingOptions = new Map<string, SelectOption>();
    const severityOptions: string[] = [];

    const normalizedIncidents = this.normalizedIncidents.filter(incident => {
      const buildingMatch =
        !this.selectedBuilding || incident.building.toLowerCase().includes(this.selectedBuilding.toLowerCase());
      const floorMatch = !this.selectedFloor || incident.floor.toLowerCase().includes(this.selectedFloor.toLowerCase());
      const roomMatch = !this.selectedRoom || incident.room.toLowerCase().includes(this.selectedRoom.toLowerCase());

      return buildingMatch && floorMatch && roomMatch;
    });

    normalizedIncidents.forEach(incident => {
      deviceTypeOptions.set(incident.deviceTypeId, {
        checked: this.deviceTypeValue.includes(incident.deviceType),
        id: incident.deviceTypeId,
        title: incident.deviceType,
        value: incident.deviceType,
      });

      buildingOptions.set(incident.building, {
        id: incident.buildingId,
        title: incident.building,
        value: incident.building,
      });

      severityOptions.push(incident.severity);
    });

    this.deviceTypeOptions = [...deviceTypeOptions.values()];

    this.severityOptions = [...new Set(severityOptions)].map(option => ({
      checked: this.severityFilter.includes(option),
      title: option.charAt(0).toUpperCase() + option.slice(1),
      value: option,
    }));
    this.buildingOptions = [defaultSelectOption, ...buildingOptions.values()];

    this.deviceTypeValue = this.deviceTypeValue.filter(deviceType =>
      this.deviceTypeOptions.find(deviceTypeOption => deviceTypeOption.value === deviceType),
    );
    this.severityFilter = this.severityFilter.filter(severity =>
      this.severityOptions.find(severityOption => severityOption.value === severity),
    );

    if (this.isBuildingTable && this.buildingOptions.length > 1) {
      this.selectedBuilding = this.buildingOptions[1].value;
      this.floorOptions = this.generateFloorOptions(this.normalizedIncidents, this.selectedBuilding);
    }

    if (this.isFloorTable && this.floorOptions.length > 1) {
      this.selectedFloor = this.floorOptions[1].value;
      this.roomOptions = this.generateRoomOptions(this.normalizedIncidents, this.selectedFloor);
    }

    if (this.selectedBuilding && !this.isBuildingTable) {
      const selectedBuildingOption = this.buildingOptions.find(
        buildingOption => buildingOption.value === this.selectedBuilding,
      );

      if (!selectedBuildingOption) {
        this.selectedBuilding = '';
        this.selectedFloor = '';
        this.selectedRoom = '';
      }
    }

    if (this.selectedFloor && !this.isFloorTable) {
      this.floorOptions = this.generateFloorOptions(this.normalizedIncidents, this.selectedBuilding);
      const selectedFloorOption = this.floorOptions.find(floorOption => floorOption.value === this.selectedFloor);

      if (!selectedFloorOption) {
        this.selectedFloor = '';
        this.selectedRoom = '';
      }
    }

    if (this.selectedRoom) {
      this.roomOptions = this.generateRoomOptions(this.normalizedIncidents, this.selectedFloor);
      const selectedRoomOption = this.roomOptions.find(roomOption => roomOption.value === this.selectedRoom);

      if (!selectedRoomOption) {
        this.selectedRoom = '';
      }
    }
  }

  generateFloorOptions(normalizedIncidents: NormalizedIncident[], selectedBuilding: string) {
    const floorOptions = new Map<string, SelectOption>();

    normalizedIncidents
      .filter(incidentData => incidentData.building === selectedBuilding)
      .forEach(incidentData => {
        floorOptions.set(incidentData.floor, {
          id: incidentData.floorId,
          title: incidentData.floor,
          value: incidentData.floor,
        });
      });

    return [defaultSelectOption, ...floorOptions.values()];
  }

  generateRoomOptions(normalizedIncidents: NormalizedIncident[], selectedFloor: string) {
    const roomOptions = new Map<string, SelectOption>();

    normalizedIncidents
      .filter(incident => incident.floor === selectedFloor)
      .forEach(incident => {
        roomOptions.set(incident.room, {
          id: incident.roomId,
          title: incident.room,
          value: incident.room,
        });
      });

    return [defaultSelectOption, ...roomOptions.values()];
  }

  dateChange() {
    this.filterOpenIncidentsTable();
  }

  updateResolvedIncidentsDeviceTypeValue(filter: { checked: boolean; value: string; }) {
    this.resolvedIncidentsDeviceTypeOptions = this.resolvedIncidentsDeviceTypeOptions.map(option => {
      if (option.value === filter.value) {
        option.checked = filter.checked;
      }

      return option;
    });
    this.filterResolvedIncidentsTable();
  }

  collectResolvedIncidentsFilterData() {
    const deviceTypeOptions: string[] = [];
    const buildingOptions: string[] = [];

    const normalizedIncidents = this.normalizedIncidents.filter(incident => {
      const siteMatch =
        !this.resolvedIncidentsSelectedBuilding ||
        incident.building.toLowerCase().includes(this.resolvedIncidentsSelectedBuilding.toLowerCase());
      const floorMatch =
        !this.resolvedIncidentsSelectedFloor ||
        incident.floor.toLowerCase().includes(this.resolvedIncidentsSelectedFloor.toLowerCase());
      const roomMatch =
        !this.resolvedIncidentsSelectedRoom ||
        incident.room.toLowerCase().includes(this.resolvedIncidentsSelectedRoom.toLowerCase());

      return siteMatch && floorMatch && roomMatch;
    });

    normalizedIncidents.forEach(incident => {
      deviceTypeOptions.push(incident.deviceType);
      buildingOptions.push(incident.building);
    });

    this.resolvedIncidentsDeviceTypeOptions = [...new Set(deviceTypeOptions)].map(option => ({
      checked: this.resolvedIncidentsDeviceTypeValue.includes(option),
      title: option,
      value: option,
    }));

    this.resolvedIncidentsDeviceTypeValue = this.resolvedIncidentsDeviceTypeValue.filter(deviceType =>
      this.resolvedIncidentsDeviceTypeOptions.find(
        deviceTypeOption => deviceTypeOption.value === deviceType,
      ),
    );

    this.resolvedIncidentsBuildingOptions = [defaultSelectOption].concat(
      [...new Set(buildingOptions)].map(option => ({ title: option, value: option })),
    );

    if (this.isBuildingTable && this.resolvedIncidentsBuildingOptions.length > 1) {
      this.resolvedIncidentsSelectedBuilding = this.resolvedIncidentsBuildingOptions[1].value;
      this.resolvedIncidentsFloorOptions = this.generateFloorOptions(
        this.normalizedIncidents,
        this.resolvedIncidentsSelectedBuilding,
      );
    }

    if (this.isFloorTable && this.resolvedIncidentsFloorOptions.length > 1) {
      this.resolvedIncidentsSelectedFloor = this.resolvedIncidentsFloorOptions[1].value;
      this.resolvedIncidentsRoomOptions = this.generateRoomOptions(
        this.normalizedIncidents,
        this.resolvedIncidentsSelectedFloor,
      );
    }

    if (this.resolvedIncidentsSelectedBuilding && !this.isBuildingTable) {
      const selectedBuildingOption = this.resolvedIncidentsBuildingOptions.find(
        buildingOption => buildingOption.value === this.resolvedIncidentsSelectedBuilding,
      );

      if (!selectedBuildingOption) {
        this.resolvedIncidentsSelectedBuilding = '';
        this.resolvedIncidentsSelectedFloor = '';
        this.resolvedIncidentsSelectedRoom = '';
      }
    }

    if (this.resolvedIncidentsSelectedFloor && !this.isFloorTable) {
      this.resolvedIncidentsFloorOptions = this.generateFloorOptions(
        this.normalizedIncidents,
        this.resolvedIncidentsSelectedBuilding,
      );
      const selectedFloorOption = this.resolvedIncidentsFloorOptions.find(
        floorOption => floorOption.value === this.resolvedIncidentsSelectedFloor,
      );

      if (!selectedFloorOption) {
        this.resolvedIncidentsSelectedFloor = '';
        this.resolvedIncidentsSelectedRoom = '';
      }
    }

    if (this.resolvedIncidentsSelectedRoom) {
      this.resolvedIncidentsRoomOptions = this.generateRoomOptions(
        this.normalizedIncidents,
        this.resolvedIncidentsSelectedFloor,
      );
      const selectedRoomOption = this.resolvedIncidentsRoomOptions.find(
        roomOption => roomOption.value === this.resolvedIncidentsSelectedRoom,
      );

      if (!selectedRoomOption) {
        this.resolvedIncidentsSelectedRoom = '';
      }
    }
  }

  filterResolvedIncidentsTable() {
    const filteredResolvedIncidents = this.normalizedIncidents.filter(incident => {
      const incidentNumberMatch =
        !this.search || incident.ticketNumber.toLowerCase().includes(this.search.toLowerCase());
      const deviceTypeMatch =
        !this.resolvedIncidentsDeviceTypeValue.length ||
        this.resolvedIncidentsDeviceTypeValue.includes(incident.deviceType);
      const siteMatch =
        !this.resolvedIncidentsSelectedBuilding ||
        incident.building.toLowerCase().includes(this.resolvedIncidentsSelectedBuilding.toLowerCase());
      const floorMatch =
        !this.resolvedIncidentsSelectedFloor ||
        incident.floor.toLowerCase().includes(this.resolvedIncidentsSelectedFloor.toLowerCase());
      const roomMatch =
        !this.resolvedIncidentsSelectedRoom ||
        incident.room.toLowerCase().includes(this.resolvedIncidentsSelectedRoom.toLowerCase());

      this.applyFiltersStatus();

      return incidentNumberMatch && deviceTypeMatch && siteMatch && floorMatch && roomMatch;
    });

    this._filteredIncidents.next(filteredResolvedIncidents);
  }

  calendarClose() {
    this.resolvedIncidentsRequest();
  }

  resolvedIncidentsRequest(forceUpdate = false) {
    if (this.resolvedDateRange.valid && this.resolvedDateRange.value.start && this.resolvedDateRange.value.end) {
      const startDate = new Date(this.resolvedDateRange.value.start);
      const endDate = new Date(this.resolvedDateRange.value.end);

      // datepicker returns start of the day, so we need to add one day to include whole day
      endDate.setDate(endDate.getDate() + 1);

      const startDateTime = startDate.toISOString();
      const endDateTime = endDate.toISOString();

      if ((this.lastRequestedResolvedStart !== startDateTime || this.lastRequestedResolvedEnd !== endDateTime) || forceUpdate) {
        this.resolvedDates$.next({
          endDateTime,
          startDateTime,
        });

        this.lastRequestedResolvedStart = startDateTime;
        this.lastRequestedResolvedEnd = endDateTime;
      }
    }
  }

  clearResolvedIncidentsFilters() {
    if (!this.isRoomTable) {
      this.resolvedIncidentsSelectedRoom = '';
    }
    if (!this.isFloorTable) {
      this.resolvedIncidentsSelectedFloor = '';
    }
    if (!this.isBuildingTable) {
      this.resolvedIncidentsSelectedBuilding = '';
    }
    this.resolvedIncidentsDeviceTypeValue = [];
    this.filterResolvedIncidentsTable();
  }

  resetState() {
    this._filteredIncidents.next([]);
    this.incidentTabStatusFilter = IncidentTabStatusFilter.OPEN;
    this.resolvedDates$.next({ endDateTime: '', startDateTime: '' });
    this.displayedColumns = incidentColumnOptions.map(option => option.value);
    this.incidentsData = [];
    this.resolvedIncidentsData = [];
    this.isBuildingTable = false;
    this.isFloorTable = false;
    this.isRoomTable = false;
    this.isReportMode = false;
    this.normalizedIncidents = [];
    this.criticalIncidentsAmount = 0;
    this.majorIncidentsAmount = 0;
    this.minorIncidentsAmount = 0;
    this.search = '';
    this.deviceTypeOptions = [];
    this.deviceTypeValue = [];
    this.assignedUserValue = [];
    this.severityOptions = [];
    this.severityFilter = [];
    this.buildingOptions = [];
    this.selectedBuilding = '';
    this.floorOptions = [];
    this.selectedFloor = '';
    this.roomOptions = [];
    this.selectedRoom = '';

    this.initialResolvedDateStart = null;
    this.initialResolvedDateEnd = null;
    // resolved incidents filters
    this.resolvedDateRange.reset();
    this.lastRequestedResolvedStart = '';
    this.lastRequestedResolvedEnd = '';
    this.resolvedIncidentsDeviceTypeOptions = [];
    this.resolvedIncidentsDeviceTypeValue = [];
    this.resolvedIncidentsBuildingOptions = [];
    this.resolvedIncidentsSelectedBuilding = '';
    this.resolvedIncidentsFloorOptions = [];
    this.resolvedIncidentsSelectedFloor = '';
    this.resolvedIncidentsRoomOptions = [];
    this.resolvedIncidentsSelectedRoom = '';
    this.resolvedFiltersInitialized = false;
    this.columnOptions = incidentColumnOptions;
  }

  private applyFiltersStatus() {
    this.filtersApplied.set(
      !!(
        this.selectedRoom.length ||
        this.selectedFloor.length ||
        this.selectedBuilding.length ||
        this.deviceTypeValue.length ||
        this.search.length
      ),
    );
  }
}
